import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Mail, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useContent, useLanguage, useUI, useLocalPath } from '../context/LanguageContext';

const AboutPage = () => {
  const content = useContent();
  const localPath = useLocalPath();
  const ui = useUI();

  return (
    <div className="pt-[72px]">
      {/* Hero */}
      <section className="relative py-24 lg:py-32" style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(59,130,246,0.04) 0%, #0d1117 70%)' }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase block mb-4">{ui.aboutLabel}</span>
            <h1 className="text-[clamp(36px,5vw,72px)] font-black leading-[0.95] tracking-[-0.02em] text-[#e6edf3] uppercase max-w-3xl">
              {ui.aboutHeroTitle[0]}<br />
              <span className="text-[#3b82f6]">{ui.aboutHeroTitle[1]}</span>
            </h1>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="font-mono text-[13px] leading-[1.8] text-[#7d8590] max-w-2xl mt-8">
            {content.aboutContent.positioning}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="flex items-center gap-10 mt-12 flex-wrap">
            {content.philosophyStats.map((stat) => (
              <div key={stat.label}>
                <span className="text-2xl font-bold text-[#3b82f6]">{stat.value}</span>
                <p className="font-mono text-[10px] tracking-[0.1em] text-[#7d8590] mt-1 uppercase">{stat.label}</p>
              </div>
            ))}
            <div>
              <span className="font-mono text-[10px] tracking-[0.1em] text-[#7d8590] uppercase">HQ</span>
              <span className="font-mono text-[10px] tracking-[0.1em] text-[#e6edf3] ml-2">Slovenia</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 border-t border-[#21262d]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase block mb-4">{ui.ourStoryLabel}</span>
                <h2 className="text-[clamp(28px,3.5vw,48px)] font-black leading-[0.95] tracking-[-0.02em]">
                  <span className="text-[#e6edf3]">{ui.fromBlogToEcosystem[0]}</span>
                  <span className="text-[#3b82f6]">{ui.fromBlogToEcosystem[1]}</span>
                </h2>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="space-y-5">
              {content.aboutContent.story.map((paragraph) => (
                <p key={paragraph.substring(0, 50)} className="font-mono text-[12px] leading-[1.8] text-[#7d8590]">{paragraph}</p>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-24 border-t border-[#21262d]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16">
            <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase block mb-4">{ui.corePhilosophyLabel}</span>
            <h2 className="text-[clamp(28px,3.5vw,48px)] font-black leading-[0.95] tracking-[-0.02em] text-[#e6edf3]">
              {content.aboutContent.philosophy.title}
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4">
            {content.aboutContent.philosophy.points.map((point, i) => (
              <motion.div key={point.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="bg-[#161b22] border border-[#21262d] rounded-lg p-6 hover:border-[#3b82f6]/30 transition-colors duration-300">
                <h3 className="text-base font-bold text-[#e6edf3] mb-2">{point.label}</h3>
                <p className="font-mono text-[11px] leading-[1.7] text-[#7d8590]">{point.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Competitive Advantage */}
      <section className="py-24 border-t border-[#21262d]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase block mb-4">{ui.whyDifferentLabel}</span>
                <h2 className="text-[clamp(28px,3.5vw,48px)] font-black leading-[0.95] tracking-[-0.02em]">
                  <span className="text-[#e6edf3]">{ui.weThinkLikeOwners[0]}</span>
                  <span className="text-[#3b82f6]">{ui.weThinkLikeOwners[1]}</span>
                </h2>
                <p className="font-mono text-[12px] leading-[1.8] text-[#7d8590] mt-6 max-w-md">{ui.whyDifferentDesc}</p>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="space-y-4">
              {content.aboutContent.advantages.map((adv) => (
                <div key={adv} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full border border-[#3b82f6]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={11} className="text-[#3b82f6]" />
                  </div>
                  <p className="font-mono text-[12px] leading-[1.7] text-[#7d8590]">{adv}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 border-t border-[#21262d]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16">
            <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase block mb-4">{ui.theTeamLabel}</span>
            <h2 className="text-[clamp(28px,3.5vw,48px)] font-black leading-[0.95] tracking-[-0.02em]">
              <span className="text-[#e6edf3]">{ui.smallTeamBigOutput[0]}</span>
              <span className="text-[#7d8590]">{ui.smallTeamBigOutput[1]}</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {content.teamMembers.map((member) => {
              const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&size=256&background=111827&color=3b82f6`;
              return (
                <motion.div key={member.name} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: content.teamMembers.indexOf(member) * 0.1 }} className="group bg-[#161b22] border border-[#21262d] rounded-lg overflow-hidden hover:border-[#3b82f6]/30 transition-all duration-500">
                  <div className="px-4 py-3 flex items-center justify-between border-b border-[#21262d]">
                    <span className="font-mono text-[10px] tracking-[0.15em] text-[#7d8590] uppercase">Go2 Digital</span>
                    <Sparkles size={12} className="text-[#3b82f6]" />
                  </div>
                  <div className="p-6 flex justify-center">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#21262d] group-hover:border-[#3b82f6]/30 transition-colors duration-500">
                      <img src={avatarUrl} alt={member.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div className="px-6 pb-6 text-center">
                    <h3 className="text-base font-bold text-[#e6edf3] mb-1">{member.name}</h3>
                    <p className="font-mono text-[10px] tracking-[0.1em] text-[#7d8590] uppercase mb-3">{member.role}</p>
                    <p className="font-mono text-[10px] tracking-[0.1em] text-[#3b82f6]">Focus: {member.focus}</p>
                    <a href="mailto:info@go2digital.com" className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.1em] text-[#7d8590] hover:text-[#3b82f6] transition-colors">
                      <Mail size={11} />
                      EMAIL ::info
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-[#21262d]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-[clamp(28px,3.5vw,48px)] font-black leading-[0.95] tracking-[-0.02em] text-[#e6edf3] mb-6">
              {ui.seeWhatWeBuilt[0]}<span className="text-[#3b82f6]">{ui.seeWhatWeBuilt[1]}</span>
            </h2>
            <Link to={localPath('/projects/')} className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.1em] uppercase bg-[#3b82f6] text-white px-8 py-3.5 rounded-full hover:bg-[#2563eb] transition-colors duration-300 font-semibold">
              {ui.viewPlatforms}
              <ArrowRight size={13} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
